/**
 * SmartCallback — exchanges auth code for access token (OpenEMR 8.0.0).
 *
 * Uses the token URL discovered during SmartLaunch (saved in localStorage)
 * to avoid URL construction mismatches.
 */
import { useEffect, useState } from 'react';

let CLIENT_ID = import.meta.env.VITE_SMART_CLIENT_ID ?? '';
let CLIENT_SECRET = import.meta.env.VITE_SMART_CLIENT_SECRET ?? '';
let REDIRECT_URI = import.meta.env.VITE_SMART_REDIRECT_URI ?? '';

async function loadRuntimeConfig() {
  try {
    const base = import.meta.env.BASE_URL.replace(/\/$/, '');
    const res = await fetch(`${base}/smart-config.json`);
    if (!res.ok) return;
    const cfg = await res.json();
    CLIENT_ID = cfg.clientId ?? CLIENT_ID;
    CLIENT_SECRET = cfg.clientSecret ?? CLIENT_SECRET;
    REDIRECT_URI = cfg.redirectUri ?? REDIRECT_URI;
  } catch { /* no runtime config */ }
}

function defaults() {
  if (!REDIRECT_URI) REDIRECT_URI = `${window.location.origin}${import.meta.env.BASE_URL.replace(/\/$/, '')}/callback`;
}

export default function SmartCallback() {
  const [error, setError] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [status, setStatus] = useState('Exchanging token...');

  useEffect(() => {
    async function handleCallback() {
      try {
        await loadRuntimeConfig();
        defaults();

        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const returnedState = urlParams.get('state');
        const oauthError = urlParams.get('error');
        const oauthErrorDesc = urlParams.get('error_description');

        // OpenEMR may return an error in the redirect
        if (oauthError) {
          throw new Error(`OAuth error: ${oauthError} — ${oauthErrorDesc ?? 'No description'}`);
        }

        if (!code) {
          throw new Error('No authorization code received from OpenEMR.');
        }

        // Validate state
        const savedState = localStorage.getItem('smart_state');
        if (savedState && returnedState && savedState !== returnedState) {
          throw new Error('State mismatch — possible CSRF. Please relaunch.');
        }

        // Get saved context from SmartLaunch
        const iss = localStorage.getItem('smart_iss') ?? import.meta.env.VITE_SMART_FHIR_URL ?? '';
        const tokenUrl = localStorage.getItem('smart_token_url') ?? '';

        if (!tokenUrl) {
          // Fallback: derive token URL from iss
          const baseUrl = iss.replace(/\/apis\/.*$/, '');
          const fallbackTokenUrl = `${baseUrl}/oauth2/default/token`;
          localStorage.setItem('smart_token_url', fallbackTokenUrl);
        }

        const finalTokenUrl = localStorage.getItem('smart_token_url')!;

        setStatus('Getting access token...');

        // Direct token exchange — same-origin (/raapid/ proxy) means no CORS issue
        const tokenBody = new URLSearchParams({
          grant_type: 'authorization_code',
          code,
          redirect_uri: REDIRECT_URI,
          client_id: CLIENT_ID,
        });
        if (CLIENT_SECRET) tokenBody.set('client_secret', CLIENT_SECRET);

        const tokenRes = await fetch(finalTokenUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: tokenBody.toString(),
        });

        if (!tokenRes.ok) {
          const errText = await tokenRes.text();
          let parsed = '';
          try {
            const errJson = JSON.parse(errText);
            parsed = errJson.error_description || errJson.error || errText;
          } catch {
            parsed = errText;
          }
          throw new Error(`Token exchange failed (${tokenRes.status}): ${parsed}`);
        }

        const tokenData = await tokenRes.json();
        if (!tokenData.access_token) {
          throw new Error('No access_token in token response.');
        }

        // Store token for app use
        localStorage.setItem('smart_access_token', tokenData.access_token);
        if (tokenData.refresh_token) {
          localStorage.setItem('smart_refresh_token', tokenData.refresh_token);
        }
        localStorage.setItem('smart_fhir_url', iss);

        // Store expiry so SmartLaunch can skip OAuth if token is still valid.
        // expires_in is in seconds (default 3600 = 1 hour in OpenEMR).
        const expiresIn = tokenData.expires_in ?? 3600;
        localStorage.setItem('smart_token_expiry', String(Date.now() + expiresIn * 1000));

        // Share token with Chrome extension content script via chrome.storage.local.
        // The content script uses it to call the FHIR API for patient context.
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const cr = (window as any).chrome;
          if (cr?.storage?.local) {
            cr.storage.local.set({
              smart_access_token: tokenData.access_token,
              smart_fhir_url: iss,
            });
          }
        } catch { /* extension not installed or not in extension context */ }

        // Clean up launch state
        localStorage.removeItem('smart_state');
        localStorage.removeItem('smart_token_url');

        setStatus('Loading patient...');

        // Get patient ID from token response or fetch first patient
        let patientId = tokenData.patient ?? '';
        if (!patientId) {
          try {
            const ptRes = await fetch(`${iss}/Patient?_count=1`, {
              headers: {
                Authorization: `Bearer ${tokenData.access_token}`,
                Accept: 'application/fhir+json',
              },
            });
            if (ptRes.ok) {
              const bundle = await ptRes.json();
              patientId = bundle.entry?.[0]?.resource?.id ?? '';
            }
          } catch {
            // Non-fatal — app can handle missing patient
          }
        }

        // Remember patient for the token-reuse flow in SmartLaunch
        if (patientId) localStorage.setItem('smart_last_patient', patientId);

        const base = import.meta.env.BASE_URL.replace(/\/$/, '');
        window.location.href = `${base}/app?mode=smart&theme=openemr${patientId ? `&patientId=${patientId}` : ''}`;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Authorization failed';
        setError(msg);

        // Provide actionable debugging info
        if (msg.includes('invalid_client')) {
          setErrorDetail(
            'The SMART app client_id may not be enabled in OpenEMR. ' +
            'Go to Admin > System > API Clients and enable RAAPID.'
          );
        } else if (msg.includes('Token exchange failed')) {
          setErrorDetail(
            'Check that redirect_uri matches exactly what was registered. ' +
            'Also verify site_addr_oath is set to https://localhost in Admin > Globals > Connectors.'
          );
        }
      }
    }

    handleCallback();
  }, []);

  if (error) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif' }}>
      <div style={{ maxWidth: '520px', textAlign: 'center', padding: '40px' }}>
        <h2 style={{ color: '#DC3545', fontSize: '18px' }}>Authorization Failed</h2>
        <p style={{ color: '#6C757D', fontSize: '14px' }}>{error}</p>
        {errorDetail && (
          <p style={{ color: '#999', fontSize: '12px', marginTop: '8px', lineHeight: '1.5' }}>{errorDetail}</p>
        )}
        <button
          onClick={() => {
            localStorage.removeItem('smart_state');
            localStorage.removeItem('smart_token_url');
            const base = import.meta.env.BASE_URL.replace(/\/$/, '');
            window.location.href = `${base}/launch?iss=` + encodeURIComponent(import.meta.env.VITE_SMART_FHIR_URL ?? '');
          }}
          style={{
            background: '#007BFF', color: 'white', border: 'none', borderRadius: '4px',
            padding: '8px 20px', cursor: 'pointer', fontSize: '13px', marginTop: '16px',
          }}
        >
          Try Again
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', background: '#F4F4F4' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '40px', height: '40px', border: '3px solid #E2E8F0', borderTopColor: '#28A745', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        <p style={{ color: '#6C757D' }}>{status}</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}
